import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useFetch } from "@/lib/content";
import { useSEO } from "@/lib/seo";
import { Eyebrow, Reveal } from "@/components/site/Motion";
import SeriesGrid from "@/components/site/SeriesGrid";

export default function Services() {
  const { data: services } = useFetch("/services");

  useSEO({
    title: "Services — L · M · S · D Series | RenB Solutions",
    description: "Four practice series for SMEs: Business Growth (L), Business Transformation (M), Strategy Planning & Execution (S), and Leadership & Executive Coaching (D).",
    path: "/services",
  });

  return (
    <div data-testid="services-page">
      <section className="border-b border-ash">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 pb-16 pt-28 md:px-10 md:pb-20 md:pt-32 lg:grid-cols-12 lg:items-center lg:gap-8">
          <Reveal className="lg:col-span-7">
            <Eyebrow>Services</Eyebrow>
            <h1 className="mt-8 max-w-4xl font-serif text-5xl font-medium leading-[1.02] tracking-tight md:text-7xl">
              Four series. One outcome: a business that runs<span className="text-progress">.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-obsidian/70 md:text-lg">
              Every RenB engagement belongs to one of four practice series. Each is a structured program with
              defined sub-programs — and each is delivered hands-on, inside your business.
            </p>
          </Reveal>
          <div className="hidden lg:col-span-4 lg:col-start-9 lg:block">
            <SeriesGrid services={services || []} />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
        {(services || []).map((s, i) => (
          <Reveal key={s.id} delay={Math.min(i * 0.05, 0.2)}>
            <Link
              to={`/services/${s.slug}`}
              data-testid={`service-row-${s.slug}`}
              className="group grid items-start gap-6 border-t border-ash py-10 transition-colors duration-300 hover:bg-ash/30 md:grid-cols-12 md:gap-8 md:py-14"
            >
              <div className="md:col-span-2">
                <span className="font-serif text-6xl font-medium leading-none text-ash transition-colors duration-300 group-hover:text-progress md:text-8xl">
                  {s.series_code}
                </span>
              </div>
              <div className="md:col-span-7">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-slategray">{s.series_code} Series</p>
                <h2 className="mt-3 font-serif text-3xl font-medium tracking-tight md:text-5xl">{s.title}</h2>
                <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-obsidian/65">{s.excerpt}</p>
              </div>
              <div className="flex items-center gap-3 md:col-span-3 md:justify-end">
                <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-progress">
                  {s.programs?.length || 0} sub-programs
                </p>
                <span className="inline-flex h-11 w-11 items-center justify-center border border-obsidian/15 transition-all duration-300 group-hover:border-progress group-hover:bg-progress group-hover:text-white">
                  <ArrowUpRight size={17} />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
        <div className="border-t border-ash" />
      </section>
    </div>
  );
}
